import React from "react";
import Icon from "./Icon";
import ModuleList from "./ModuleList";
import { RowMenu, StatusChip, Switch } from "./controls";
import { groupByFamily } from "./catalogModel";
import { actionState, menuItems, productLabel } from "./ProductActions";
import CourseCell, { CourseActions } from "./CourseCell";

const COLUMNS = [44, 36, null, 84, 118, 84, 176, 170, 152];
// With the course column and its actions the fixed columns must leave the product name room (about 240px in a 1190px page),
// so the hosted layout is tighter than the standalone one.
const HOSTED_COLUMNS = [40, 32, null, 64, 96, 64, 130, 175, 150, 290];

function ProductRow({ product, selected, onSelect, expanded, onExpand, saving, onVerify, onToggle, onEdit, course }) {
  const label = productLabel(product);
  const { status, canVerify, canToggle, active } = actionState(product);
  const inactive = status === "inactive";
  const tip = inactive ? "Reactivate the product first" : "Verify the product number first";
  const verifyButton = (
    <button type="button" className="ac-sbtn" disabled={saving} aria-label={`Verify ${label}`} onClick={() => onVerify(product)}>
      <Icon n="shield" s={14} />Verify
    </button>
  );
  return (
    <tr data-testid={`aha-row-${product.key}`} className={`${selected ? "sel " : ""}${expanded ? "exp " : ""}`}>
      <td className="c-chk"><input type="checkbox" checked={selected} onChange={onSelect} aria-label={`Select ${label}`} /></td>
      <td className="c-exp">
        <button type="button" className="ac-ib sm" aria-expanded={expanded} aria-label={`${expanded ? "Collapse" : "Expand"} ${product.name}`} onClick={onExpand}>
          <Icon n={expanded ? "chevDown" : "chevRight"} s={15} />
        </button>
      </td>
      <td className="c-name"><b>{product.name}</b><span className="mono">{product.key}</span></td>
      <td>{product.edition || <span className="muted">—</span>}</td>
      <td className="c-num mono">{product.environment?.productNumber ?? "—"}</td>
      <td><button type="button" className="ac-link" aria-label={`Show modules for ${label}`} onClick={onExpand}>{(product.links || []).length}</button></td>
      <td><StatusChip status={status} /></td>
      <td>
        <span className="ac-live">
          <Switch on={active} disabled={!canToggle || saving} tip={tip} label={`Active for all training centers: ${product.name}`} onChange={(next) => onToggle(product, next)} />
          <span className={canVerify ? "ac-hint" : "ac-live-l"}>{canVerify ? "Verify first" : active ? "On" : "Off"}</span>
        </span>
      </td>
      {course && <td><CourseCell product={product} {...course} /></td>}
      <td className="c-act">
        {course ? (
          // Three fixed slots (course actions, Verify, menu) so every row lines up whichever of them it has.
          <div className="ac-actgrid">
            <span className="ac-slot"><CourseActions product={product} {...course} /></span>
            <span className="ac-slot">{canVerify && verifyButton}</span>
            <span className="ac-slot"><RowMenu label={`More actions for ${product.name}`} items={menuItems(product, { onEdit, onToggle })} /></span>
          </div>
        ) : (
          <>
            {canVerify && verifyButton}
            <RowMenu label={`More actions for ${product.name}`} items={menuItems(product, { onEdit, onToggle })} />
          </>
        )}
      </td>
    </tr>
  );
}

function DetailRow({ product, wide }) {
  return (
    <tr className="ac-detail"><td /><td />
      <td colSpan={wide ? 8 : 7}>
        <div className="ac-det">
          <div><h5>Requires subcourse</h5><p>{product.requiresSubcourse ? "Yes" : "No"}</p></div>
          <div><h5>Modules and AHA IDs</h5><ModuleList productKey={product.key} /></div>
        </div>
      </td>
    </tr>
  );
}

/** Product table; `grouped` splits it into collapsible family sections. */
export default function ProductTable({ rows, grouped, collapsed, onCollapsed, selected, onSelected, expanded, onExpanded, savingKey, errors, onVerify, onToggle, onEdit, course }) {
  const columns = course ? HOSTED_COLUMNS : COLUMNS;
  const span = columns.length;
  const allSelected = rows.length > 0 && rows.every((p) => selected.includes(p.key));
  const someSelected = rows.some((p) => selected.includes(p.key));
  const flip = (list, key) => (list.includes(key) ? list.filter((x) => x !== key) : [...list, key]);
  const groups = grouped ? groupByFamily(rows) : [[null, rows]];

  return (
    <div className="ac-tbl-wrap">
      <table className={`ac-tbl${course ? " hosted" : ""}`} aria-label="AHA products">
        <colgroup>{columns.map((w, i) => <col key={i} style={w ? { width: w } : undefined} />)}</colgroup>
        <thead>
          <tr>
            <th className="c-chk">
              <input type="checkbox" checked={allSelected} ref={(el) => { if (el) el.indeterminate = someSelected && !allSelected; }}
                onChange={() => onSelected(allSelected ? [] : rows.map((p) => p.key))} aria-label="Select all visible products" />
            </th>
            <th /><th>Product</th><th>Edition</th><th>AHA number</th><th>Modules</th><th>Status</th><th>Active for centers</th>
            {course && <th>Standard course</th>}
            <th className="c-act"><span className="sr">Actions</span></th>
          </tr>
        </thead>
        <tbody>
          {groups.map(([family, list]) => {
            const isCollapsed = !!family && collapsed.includes(family);
            const activeCount = list.filter((p) => actionState(p).active).length;
            return (
              <React.Fragment key={family || "all"}>
                {family && (
                  <tr className="ac-grp">
                    <td colSpan={span}>
                      <button type="button" aria-expanded={!isCollapsed} onClick={() => onCollapsed(flip(collapsed, family))}>
                        <Icon n={isCollapsed ? "chevRight" : "chevDown"} s={15} />
                        <b>{family}</b><span>{list.length} product{list.length === 1 ? "" : "s"}</span>
                        <span>·</span><span className={activeCount ? "g" : ""}>{activeCount} active</span>
                      </button>
                    </td>
                  </tr>
                )}
                {!isCollapsed && list.map((p) => (
                  <React.Fragment key={p.key}>
                    <ProductRow product={p} selected={selected.includes(p.key)} onSelect={() => onSelected(flip(selected, p.key))}
                      expanded={expanded.includes(p.key)} onExpand={() => onExpanded(flip(expanded, p.key))}
                      saving={savingKey === p.key} onVerify={onVerify} onToggle={onToggle} onEdit={onEdit} course={course} />
                    {errors[p.key] && <tr className="ac-rowerr"><td /><td /><td colSpan={span - 2}><div className="ac-err" role="alert">{errors[p.key]}</div></td></tr>}
                    {expanded.includes(p.key) && <DetailRow product={p} wide={Boolean(course)} />}
                  </React.Fragment>
                ))}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
